import { ElementChange, ElementChangeStatus } from '@gearbelt/global';

import type { BadgeProps } from '@/components/ui/badge';

type RevisionBadge = { variant: BadgeProps['variant']; label: string; className?: string };

// Maps an element's change to the standard <Badge> variant + label shown in the
// diff columns. Added/Deleted are solid dark green/red with white text; moved
// up=blue ↑N, moved down=orange ↓N. An in-place edit carries no badge — its blue
// host tint is the cue (a badge would collide with the move arrow). The shared
// medium weight is applied at the render site, so each case here carries only its
// case-specific colours.
export function revisionBadgeProps(change: ElementChange | undefined): RevisionBadge | null {
  switch (change?.status) {
    case ElementChangeStatus.Added:
      return { variant: 'success', label: 'Added', className: 'bg-default-dark-green hover:bg-default-dark-green text-white' };
    case ElementChangeStatus.Deleted:
      return { variant: 'destructive', label: 'Deleted', className: 'bg-default-dark-red hover:bg-default-dark-red text-white' };
    case ElementChangeStatus.Moved:
      return change.moveOffset < 0 ? { variant: 'default', label: `↑${Math.abs(change.moveOffset)}` } : { variant: 'orange', label: `↓${change.moveOffset}` };
    default:
      return null;
  }
}

// Host tint for the element wrapper. The colour tracks the element's CONTENT state —
// green=added, red=deleted, blue=edited-in-place (label / required / config changed),
// grey=present-and-unchanged — independent of position; the move arrow (badge) conveys
// position. A surviving element (Moved or Unchanged) is therefore blue when edited and
// neutral grey otherwise, so every element in a diff column reads as a uniform box.
// No decoration (non-diff usage, e.g. form fill) renders no wrapper.
export function revisionTintClassName(change: ElementChange | undefined): string {
  switch (change?.status) {
    case ElementChangeStatus.Added:
      return 'rounded-md border border-default-green bg-default-light-green p-3';
    case ElementChangeStatus.Deleted:
      return 'rounded-md border border-default-red bg-default-light-red p-3';
    case ElementChangeStatus.Moved:
    case ElementChangeStatus.Unchanged:
      return change.contentChanged ? 'rounded-md border border-default-blue bg-default-light-blue p-3' : 'rounded-md border border-grey-3 bg-grey-1 p-3';
    default:
      return '';
  }
}

// Activity-list label: revision 0 is the creation (v1); each later revision is an edit.
export function revisionActivityLabel(revision: number): string {
  return revision === 0 ? 'Created v1' : `Edited v${revision}`;
}
